import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { NegotiationCampaign, VendorContact, VendorTier } from "@/types/negotiations";
import { CooldownError, type CreateThreadInput } from "@/hooks/useNegotiations";

const QUERY_KEY = ["negotiation_campaigns"] as const;

// Only select the columns that exist in the negotiation_campaigns table schema:
// id (default), company_id (default current_user_company_id()), name (NOT NULL),
// created_by (default auth.uid()), created_at (default).
const CAMPAIGN_COLUMNS = "id, name, company_id, created_by, created_at";

async function fetchCampaigns(): Promise<NegotiationCampaign[]> {
  const { data, error } = await supabase
    .from("negotiation_campaigns")
    .select(CAMPAIGN_COLUMNS)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return data ?? [];
}

/** Options for bulk thread creation in a campaign. */
export interface StartCampaignOptions extends Omit<
  CreateThreadInput,
  "supplier_email" | "vendor_name" | "subject"
> {
  /** Campaign name (inserted into `negotiation_campaigns`). */
  campaign_name: string;
  /** Subject line template. `{vendor_name}` is replaced with the actual vendor name. */
  subject_template: string;
  /**
   * Skip strategic-tier vendors.
   * Per TECHNICAL §8c: strategic defaults to draft_wait; exclude from bulk unless
   * explicitly opted in by setting this to false.
   */
  skipStrategic?: boolean;
}

export interface CampaignStartResult {
  campaign: NegotiationCampaign;
  started: string[];
  skipped: Array<{ vendor_name: string; reason: string }>;
}

async function fetchContacts(): Promise<VendorContact[]> {
  const { data, error } = await supabase.from("vendor_contacts").select("*");

  if (error) throw error;
  return (data ?? []) as VendorContact[];
}

export function useNegotiationCampaigns() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const {
    data: campaigns = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: fetchCampaigns,
  });

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: ["negotiation_threads"] });
  }

  const createCampaignMutation = useMutation({
    mutationFn: async (name: string): Promise<NegotiationCampaign> => {
      // Insert only `name`; company_id, created_by, and created_at are DB defaults.
      const { data, error } = await supabase
        .from("negotiation_campaigns")
        .insert({ name })
        .select(CAMPAIGN_COLUMNS)
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      invalidate();
    },
    onError: (err) => {
      toast({
        title: "Failed to create campaign",
        description: (err as Error).message,
        variant: "destructive",
      });
    },
  });

  /**
   * Bulk-launch a campaign: creates a `negotiation_campaigns` row, then
   * loops `createThread` for each vendor name, respecting:
   *   - contact presence (must have a supplier_email)
   *   - tier (skips strategic by default)
   *   - mode (honours vendor contact mode)
   *   - decline cooldown (skips rather than blocking the whole campaign)
   *
   * Returns { campaign, started[], skipped[] } so the caller can show a summary.
   */
  const startCampaignMutation = useMutation({
    mutationFn: async ({
      vendorNames,
      opts,
      createThread,
    }: {
      vendorNames: string[];
      opts: StartCampaignOptions;
      createThread: (input: CreateThreadInput) => Promise<unknown>;
    }): Promise<CampaignStartResult> => {
      const { skipStrategic = true, campaign_name, subject_template, ...threadOpts } = opts;

      // 1. Create the campaign record
      const campaign = await createCampaignMutation.mutateAsync(campaign_name);

      // 2. Load all vendor contacts for lookup
      const contacts = await fetchContacts();
      const contactMap = new Map<string, VendorContact>(contacts.map((c) => [c.vendor_name, c]));

      const started: string[] = [];
      const skipped: Array<{ vendor_name: string; reason: string }> = [];

      // 3. Process each vendor sequentially (respect server-side cooldown queries)
      for (const vendor_name of vendorNames) {
        const contact = contactMap.get(vendor_name);

        // Must have a contact email
        if (!contact?.supplier_email) {
          skipped.push({ vendor_name, reason: "no contact email" });
          continue;
        }

        // Skip excluded vendors
        if (contact.excluded) {
          skipped.push({ vendor_name, reason: "excluded" });
          continue;
        }

        // Skip strategic vendors (TECHNICAL §8c)
        if (skipStrategic && (contact.tier as VendorTier) === "strategic") {
          skipped.push({ vendor_name, reason: "strategic tier — skipped from bulk" });
          continue;
        }

        const subject = subject_template.replace("{vendor_name}", vendor_name);

        try {
          await createThread({
            ...threadOpts,
            supplier_email: contact.supplier_email,
            vendor_name,
            subject,
          } as CreateThreadInput & { campaign_id?: string });

          // Patch the thread we just created with campaign_id
          // (createThread doesn't accept campaign_id directly, so we update after)
          const { data: latestThread } = await supabase
            .from("negotiation_threads")
            .select("id")
            .eq("vendor_name", vendor_name)
            .order("created_at", { ascending: false })
            .limit(1)
            .maybeSingle();

          if (latestThread) {
            await supabase
              .from("negotiation_threads")
              .update({ campaign_id: campaign.id })
              .eq("id", latestThread.id);
          }

          started.push(vendor_name);
        } catch (err) {
          if (err instanceof CooldownError) {
            skipped.push({ vendor_name, reason: `cooldown active (${err.message})` });
          } else {
            skipped.push({
              vendor_name,
              reason: `error: ${(err as Error).message}`,
            });
          }
        }
      }

      return { campaign, started, skipped };
    },
    onSuccess: ({ started, skipped }) => {
      invalidate();
      toast({
        title: "Campaign launched",
        description: `${started.length} started, ${skipped.length} skipped.`,
      });
    },
    onError: (err) => {
      toast({
        title: "Campaign failed",
        description: (err as Error).message,
        variant: "destructive",
      });
    },
  });

  return {
    campaigns,
    isLoading,
    error,
    refetch,

    /** Create a named campaign record. */
    createCampaign: (name: string) => createCampaignMutation.mutateAsync(name),

    /**
     * Bulk-launch threads for a list of vendor names.
     * Pass `createThread` from `useNegotiations()` so the cooldown / derivation logic
     * is centralised in that hook.
     */
    startCampaign: (
      vendorNames: string[],
      opts: StartCampaignOptions,
      createThread: (input: CreateThreadInput) => Promise<unknown>
    ) => startCampaignMutation.mutateAsync({ vendorNames, opts, createThread }),

    isCreatingCampaign: createCampaignMutation.isPending,
    isStartingCampaign: startCampaignMutation.isPending,
  };
}
